import { BALANCE, BUILDING_DEFS, COLS, MAX_DEPTH } from '../constants';
import { computeLegacy } from '../legacy';
import type { Simulation } from '../sim';
import type { EndingKind, SimState } from '../types';
import { pushLedger } from './economy';
import { tierWageCents } from './tiers';

/** Market value of every deciton still in the ground. */
export function remainingOreValueCents(state: SimState): number {
  let ironDt = 0;
  let goldDt = 0;
  for (let cell = 0; cell < COLS * MAX_DEPTH; cell += 1) {
    const type = state.terrain.oreType[cell] ?? 0;
    if (type === 1) ironDt += state.terrain.oreAmount[cell] ?? 0;
    else if (type === 2) goldDt += state.terrain.oreAmount[cell] ?? 0;
  }
  return Math.floor((ironDt * state.economy.market.iron.price + goldDt * state.economy.market.gold.price) / 10);
}

/** What the MINE costs to run: miner/hauler wages + fuel. This is the number
 * HQ judges the pull-out by — building farms must never hasten the ultimatum,
 * so industry wages are deliberately out of scope here. */
export function mineWeeklyExpensesCents(sim: Simulation): number {
  let wages = 0;
  for (const peep of sim.state.peeps) {
    if (peep.job === 'miner' || peep.job === 'hauler') wages += tierWageCents(peep);
  }
  let fuel = 0;
  for (const building of sim.state.buildings) {
    if (building.type === 'generator') fuel += BUILDING_DEFS.generator.fuelCentsPerWeek ?? 0;
  }
  return wages + fuel;
}

/** What the whole operation costs to run: every tier wage (mine AND industry)
 * plus every fuel bill. The Endures test measures the town against this, so
 * an industrial town has to actually cover its payroll to make history. */
export function weeklyExpensesCents(sim: Simulation): number {
  let wages = 0;
  for (const peep of sim.state.peeps) wages += tierWageCents(peep);
  let fuel = 0;
  for (const building of sim.state.buildings) {
    fuel += BUILDING_DEFS[building.type].fuelCentsPerWeek ?? 0;
  }
  return wages + fuel;
}

export function buyoutPriceCents(sim: Simulation): number {
  const state = sim.state;
  const bankOpen = state.buildings.some((building) => building.type === 'bank' && building.functional);
  let price =
    BALANCE.buyoutBaseCents +
    state.buildings.length * BALANCE.buyoutPerBuildingCents +
    Math.min(BALANCE.buyoutOreCapCents, Math.floor(remainingOreValueCents(state) * BALANCE.buyoutOreFraction));
  if (bankOpen) price = Math.floor(price * (1 - BALANCE.buyoutBankDiscount));
  return Math.floor(price * (1 + BALANCE.buyoutInterest));
}

export function stripSettlementCents(sim: Simulation): number {
  const state = sim.state;
  return (
    state.buildings.length * BALANCE.stripPerBuildingCents +
    Math.min(BALANCE.stripOreCapCents, Math.floor(remainingOreValueCents(state) * BALANCE.stripOreFraction))
  );
}

export function recordEnding(sim: Simulation, kind: EndingKind): void {
  const meta = sim.state.meta;
  if (meta.ending !== null) return;
  meta.ending = {
    kind,
    week: sim.week,
    townName: meta.townName ?? 'the camp',
    legacy: computeLegacy(sim, kind),
  };
  sim.pushEvent({ kind: 'endingReached', ending: kind });
}

export function resolvePullout(sim: Simulation, choice: 'strip' | 'buyout' | 'wait'): void {
  const state = sim.state;
  if (state.meta.era !== 'pullout' || state.meta.ending !== null) return;
  if (choice === 'wait') return;
  if (choice === 'strip') {
    const settlement = stripSettlementCents(sim);
    state.economy.cashCents += settlement;
    pushLedger(state, sim.day, 'reward', settlement, 'HQ final settlement');
    state.meta.era = 'independent'; // HQ is gone either way; the site is closed
    state.letters.quota = null;
    recordEnding(sim, 'strip');
    return;
  }
  // Buyout on installments: a mortgage on your own town.
  const total = buyoutPriceCents(sim);
  state.meta.era = 'installments';
  state.meta.installments = {
    totalCents: total,
    weeklyCents: Math.ceil(total / BALANCE.buyoutWeeks),
    paidWeeks: 0,
    totalWeeks: BALANCE.buyoutWeeks,
    missedWeeks: 0,
  };
  sim.pushEvent({ kind: 'toast', message: `Buyout signed: ${BALANCE.buyoutWeeks} weekly payments to HQ.` });
}

/** Weekly, after payroll: the whole depletion arc and both late endings. */
export function stepEndgameWeekly(sim: Simulation): void {
  const state = sim.state;
  const meta = state.meta;
  const town = state.town;
  if (meta.gameOver) return;

  const mineAvg = town.mineIncomeHistory.reduce((a, b) => a + b, 0) / Math.max(1, town.mineIncomeHistory.length);
  const townAvg = town.townIncomeHistory.reduce((a, b) => a + b, 0) / Math.max(1, town.townIncomeHistory.length);

  // --- Company era: watch for the pull-out moment.
  if (meta.era === 'company') {
    if (
      sim.week >= BALANCE.pulloutMinWeek &&
      town.mineIncomeHistory.length >= 4 &&
      (remainingOreValueCents(state) < BALANCE.pulloutOreValueThresholdCents ||
        mineAvg < mineWeeklyExpensesCents(sim) * BALANCE.pulloutIncomeFraction)
    ) {
      meta.era = 'pullout';
      meta.pulloutWeek = sim.week;
      sim.pushEvent({ kind: 'pulloutOffered' });
      sim.pushEvent({ kind: 'toast', message: 'URGENT: HQ is closing the site. Answer their letter.' });
    }
    return;
  }

  // --- Pull-out pending: HQ nags until you decide.
  if (meta.era === 'pullout') {
    if (meta.pulloutWeek !== null && (sim.week - meta.pulloutWeek) % BALANCE.pulloutNagWeeks === 0) {
      sim.pushEvent({ kind: 'toast', message: 'HQ is waiting for your answer on the site closure.' });
    }
  }

  // --- Installments: pay the mortgage.
  if (meta.era === 'installments' && meta.installments) {
    const plan = meta.installments;
    if (state.economy.cashCents >= plan.weeklyCents) {
      state.economy.cashCents -= plan.weeklyCents;
      plan.paidWeeks += 1;
      pushLedger(state, sim.day, 'interest', -plan.weeklyCents, `Buyout installment ${plan.paidWeeks}/${plan.totalWeeks}`);
      if (plan.paidWeeks >= plan.totalWeeks) {
        meta.era = 'independent';
        meta.installments = null;
        state.letters.quota = null;
        sim.pushEvent({ kind: 'independence' });
        sim.pushEvent({ kind: 'toast', message: `${meta.townName ?? 'The town'} belongs to its people now.` });
      }
    } else {
      plan.missedWeeks += 1;
      plan.totalCents += Math.floor(plan.totalCents * BALANCE.buyoutMissPenalty);
      plan.weeklyCents = Math.ceil(plan.totalCents / plan.totalWeeks);
      sim.pushEvent({ kind: 'toast', message: 'Missed a buyout payment. HQ added penalty interest.' });
    }
  }

  // --- Endings armed once HQ no longer owns the outcome.
  const endingsArmed = meta.era === 'independent' || meta.era === 'installments' || meta.era === 'pullout';
  if (!endingsArmed || meta.ending !== null) return;

  // Ghost-town collapse.
  if (state.peeps.length < BALANCE.ghostPopMin) {
    recordEnding(sim, 'ghost');
    return;
  }
  if (state.economy.cashCents < 0) {
    town.insolventStreak += 1;
    if (town.insolventStreak >= BALANCE.ghostInsolventWeeks) {
      recordEnding(sim, 'ghost');
      return;
    }
  } else {
    town.insolventStreak = 0;
  }

  // The Endure test only runs for a town that owns itself.
  if (meta.era !== 'independent') return;
  const mineShare = mineAvg / Math.max(1, mineAvg + townAvg);
  if (mineShare < BALANCE.endureMineShareMax) town.postOreWeeks += 1;
  const selfSufficient =
    townAvg >= weeklyExpensesCents(sim) * 0.9 &&
    mineShare < BALANCE.endureMineShareMax &&
    state.peeps.length >= BALANCE.endurePopMin &&
    state.workforce.morale >= BALANCE.endureMoraleMin;
  town.endureStreak = selfSufficient ? town.endureStreak + 1 : 0;
  if (town.endureStreak >= BALANCE.endureWeeksNeeded) {
    recordEnding(sim, 'endures');
  }
}
